import { cn } from "utils/cn";
import { ProjectState } from "hooks/useProjects/useProjects.types";

const MAX_VISIBLE = 3;

export interface TeamStackMember {
  id: string | number;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}

/** Normalize whichever member source a project carries (summary endpoint or legacy team). */
export const getTeamStackMembers = (project: ProjectState): TeamStackMember[] => {
  if (project.teamSummary?.length) {
    return project.teamSummary.map((member) => ({
      id: member.userId,
      firstName: member.firstName,
      lastName: member.lastName,
    }));
  }
  return (project.team?.members ?? []).map((member) => ({
    id: member.team_member_id,
    firstName: member.invitation?.invitee?.first_name,
    lastName: member.invitation?.invitee?.last_name,
    email: member.invitation?.invitee?.email,
  }));
};

const memberName = (member: TeamStackMember) => {
  const name = [member.firstName, member.lastName].filter(Boolean).join(" ").trim();
  return name || member.email || "";
};

const memberInitials = (member: TeamStackMember) => {
  const initials = `${member.firstName?.[0] ?? ""}${member.lastName?.[0] ?? ""}`.trim();
  return (initials || member.email?.[0] || "?").toUpperCase();
};

export interface ProjectTeamStackProps {
  members: TeamStackMember[];
  className?: string;
}

/** Compact read-only stack of who has access. Sharing lives in project settings. */
export const ProjectTeamStack = ({ members, className }: ProjectTeamStackProps) => {
  if (members.length === 0) {
    return null;
  }

  const visible = members.slice(0, MAX_VISIBLE);
  const extra = members.length - visible.length;
  const names = members.map(memberName).filter(Boolean).join(", ");

  return (
    <span
      className={cn("flex items-center -space-x-1.5", className)}
      role="img"
      aria-label={`${members.length} ${members.length === 1 ? "person has" : "people have"} access`}
      title={names ? `Access: ${names}` : undefined}
    >
      {visible.map((member) => (
        <span
          key={member.id}
          className="flex h-5 w-5 items-center justify-center rounded-md border border-card bg-muted text-[9px] font-semibold text-muted-foreground"
        >
          {memberInitials(member)}
        </span>
      ))}
      {extra > 0 && (
        <span className="flex h-5 min-w-5 items-center justify-center rounded-md border border-card bg-muted px-0.5 text-[9px] font-semibold text-muted-foreground">
          +{extra}
        </span>
      )}
    </span>
  );
};
